import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useIsMobile } from '../hooks/useIsMobile';
import logo from '../assets/logo.svg';
import { DashboardIcon, UsersIcon, BookIcon, EnvelopeIcon, SparklesConfigIcon, MenuIcon, CloseIcon } from '../assets/icons';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  // Close the drawer whenever the route changes (e.g. after tapping a nav item).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Prevent body scroll while the mobile drawer is open.
  useEffect(() => {
    if (isMobile && open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isMobile, open]);

  const navItems: NavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/app',
      icon: <DashboardIcon size={20} />,
    },
    {
      id: 'patients',
      label: 'Mis Pacientes',
      path: '/app/patients',
      icon: <UsersIcon size={20} />,
    },
    {
      id: 'resources',
      label: 'Recursos',
      path: '/app/resources',
      icon: <BookIcon size={20} />,
    },
    ...(user?.role === 'admin'
      ? [
          {
            id: 'invites',
            label: 'Invitaciones',
            path: '/app/invites',
            icon: <EnvelopeIcon size={20} />,
          },
          {
            id: 'agent-config',
            label: 'Agente',
            path: '/app/agent-config',
            icon: <SparklesConfigIcon size={20} />,
          },
        ]
      : []),
  ];

  const isActive = (path: string) => {
    if (path === '/app') {
      return location.pathname === '/app';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (firstName?: string, lastName?: string, email?: string) => {
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <>
      {/* Mobile hamburger toggle */}
      {isMobile && (
        <button
          aria-label="Abrir menú"
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 120,
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            color: '#1f2937',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          <MenuIcon size={22} />
        </button>
      )}

      {/* Backdrop overlay (mobile, drawer open) */}
      {isMobile && open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            zIndex: 100,
          }}
        />
      )}

      <aside
        style={{
          width: 240,
          maxWidth: '80vw',
          height: '100vh',
          background: '#fff',
          borderRight: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 110,
          transform: isMobile && !open ? 'translateX(-100%)' : 'translateX(0)',
          transition: 'transform 0.25s ease',
          boxShadow: isMobile && open ? '2px 0 16px rgba(0,0,0,0.12)' : 'none',
        }}
      >
      {/* Logo and Title */}
      <div
        style={{
          padding: '24px 20px',
          borderBottom: '1px solid #f3f4f6',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <img src={logo} alt="Sissi Health Logo" style={{ width: 32, height: 32 }} />
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 18, color: '#1f2937' }}>Sissi Health</span>
          {user?.role === 'admin' ? (
            <span
              style={{
                display: 'inline-block',
                alignSelf: 'flex-start',
                background: '#ede9fe',
                color: '#6d28d9',
                padding: '1px 8px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                marginTop: 3,
              }}
            >
              Admin
            </span>
          ) : (
            user?.clinic && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#6b7280',
                  marginTop: 2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user.clinic.name}
              </span>
            )
          )}
        </div>
        {isMobile && (
          <button
            aria-label="Cerrar menú"
            onClick={() => setOpen(false)}
            style={{
              marginLeft: 'auto',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: 'pointer',
            }}
          >
            <CloseIcon size={22} />
          </button>
        )}
      </div>

      {/* Navigation Items */}
      <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                marginBottom: 4,
                background: active ? '#f0fdfa' : 'transparent',
                color: active ? '#38b2ac' : '#6b7280',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 15,
                fontWeight: active ? 600 : 500,
                transition: 'all 0.2s',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.color = '#374151';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
                }
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* User Info Block */}
      <div
        style={{
          padding: '16px 12px',
          borderTop: '1px solid #f3f4f6',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px',
            background: '#f9fafb',
            borderRadius: 8,
            marginBottom: 8,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#38b2ac',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              fontWeight: 600,
              color: '#fff',
              flexShrink: 0,
            }}
          >
            {getInitials(user?.firstName, user?.lastName, user?.email)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 14,
                color: '#1f2937',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}`
                : user?.email}
            </div>
            {user?.role && (
              <div
                style={{
                  display: 'inline-block',
                  background: '#e0f2f1',
                  color: '#00796b',
                  padding: '2px 8px',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  marginTop: 2,
                }}
              >
                {user.role}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: 'transparent',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            color: '#6b7280',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f9fafb';
            e.currentTarget.style.borderColor = '#d1d5db';
            e.currentTarget.style.color = '#374151';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.color = '#6b7280';
          }}
        >
          Cerrar sesión
        </button>
      </div>
      </aside>
    </>
  );
};

export default Sidebar;
